import React, { useEffect, useState } from 'react';

const TICKER_KEYFRAMES = `
@keyframes pctScroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
`;

let tickerStyleInjected = false;
function injectTickerStyles() {
  if (tickerStyleInjected || typeof document === 'undefined') return;
  tickerStyleInjected = true;
  const el = document.createElement('style');
  el.textContent = TICKER_KEYFRAMES;
  document.head.appendChild(el);
}

const formatTickerDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const buildTickerLabel = (entry) => {
  const parts = [];
  if (entry.name) parts.push(entry.name);
  if (entry.vendor) parts.push(entry.vendor);
  if (entry.unit) parts.push(entry.unit);

  const oldP = entry.oldPrice;
  const newP = entry.newPrice;
  const hasPrev = oldP != null && oldP > 0;

  if (hasPrev) {
    parts.push(`$${oldP.toFixed(2)} \u2192 $${newP.toFixed(2)}`);
    const delta = newP - oldP;
    const sign = delta >= 0 ? '+' : '\u2212';
    const pct = (delta / oldP) * 100;
    const pctSign = pct >= 0 ? '+' : '\u2212';
    parts.push(`${sign}$${Math.abs(delta).toFixed(2)} / ${pctSign}${Math.abs(pct).toFixed(1)}%`);
  } else {
    parts.push(`$${newP.toFixed(2)} \u2014 new price`);
  }

  if (entry.date) parts.push(formatTickerDate(entry.date));

  return parts.join(' \u00B7 ');
};

const MAX_DISPLAY = 20;
const MAX_DURATION = 75; // seconds cap - prevents 100+ entries from crawling for 10+ minutes

const PriceChangeTicker = ({ entries }) => {
  const [dismissed, setDismissed] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    injectTickerStyles();
  }, []);

  if (dismissed || !entries || entries.length === 0) return null;

  // Cap to recent 20 to prevent a 50k+px wide nowrap span and huge animation
  const displayEntries = entries.slice(0, MAX_DISPLAY);
  const labels = displayEntries.map(buildTickerLabel);

  const itemStyle = {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    padding: '0 1.5rem',
    fontSize: '0.83rem',
    color: '#7A6A62',
    letterSpacing: '0.01em',
    maxWidth: 'none',
    boxSizing: 'border-box',
  };

  const dotStyle = {
    color: '#000',
    fontWeight: 700,
  };

  const renderLoop = (keyPrefix) => (
    <span style={itemStyle}>
      {labels.map((label, i) => (
        <React.Fragment key={`${keyPrefix}-${i}`}>
          {i > 0 && (
            <span style={dotStyle}>
              {'\u00A0\u00A0\u00A0\u00A0\u25AA\u00A0\u00A0\u00A0\u00A0'}
            </span>
          )}
          {label}
        </React.Fragment>
      ))}
    </span>
  );

  // Was entries.length * 7.5 -> 200 entries = 1500s. Cap to keep it readable and performant
  const duration = Math.min(MAX_DURATION, Math.max(22.5, displayEntries.length * 3));

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      background: '#F5F0EC',
      border: '1px solid #E2D8D0',
      borderRadius: '8px',
      marginBottom: '0.75rem',
      overflow: 'hidden',
      height: '28px',
      minWidth: 0,
      maxWidth: '100%',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative',
      contain: 'layout paint',
    }}>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss price ticker"
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0 0.45rem',
          color: '#A89890',
          fontSize: '0.8rem',
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          borderRight: '1px solid #E2D8D0',
          fontFamily: 'inherit',
        }}
      >
        &#x2715;
      </button>

      <div
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{
          flex: 1,
          width: 0,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            flex: '0 0 auto',
            width: 'max-content',
            animation: `pctScroll ${duration}s linear infinite`,
            animationPlayState: paused ? 'paused' : 'running',
            willChange: 'transform',
            maxWidth: 'none',
            boxSizing: 'border-box',
          }}
        >
          {renderLoop('a')}
          <span aria-hidden="true" style={{ display: 'contents' }}>
            {renderLoop('b')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PriceChangeTicker;
